import React, { useCallback, useEffect, useState } from 'react';
import { fromDatetimeLocalVancouver, toDatetimeLocalVancouver } from '../../lib/utils';
import { useAdminApi, errorMessage } from './adminApi';
import { useQueryParams } from './useQueryParams';
import { formatDateTime } from './format';
import {
  Badge, DataTable, Drawer, ListLayout, Pagination, Panel, SearchInput, SegmentedControl, Spinner, Toolbar,
  btnDanger, btnPrimary, btnSecondary, inputClass, labelClass
} from './AdminUI';

const EMPTY_EVENT = { title: '', description: '', startTime: '', endTime: '', recurrence: 'none' };

function EventForm({ condo, event, onSaved, onCancel }) {
  const api = useAdminApi();
  const [form, setForm] = useState(() => (event ? {
    title: event.title || '',
    description: event.description || '',
    startTime: toDatetimeLocalVancouver(event.startTime),
    endTime: toDatetimeLocalVancouver(event.endTime),
    recurrence: 'none'
  } : EMPTY_EVENT));
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title,
      description: form.description,
      startTime: fromDatetimeLocalVancouver(form.startTime).toISOString(),
      endTime: fromDatetimeLocalVancouver(form.endTime).toISOString()
    };
    try {
      if (event) await api.put(`/api/calendar/event/${event._id}`, payload);
      else await api.post(`/api/calendar/${condo._id}`, { ...payload, recurrence: form.recurrence });
      onSaved('Calendar event submitted for approval. Review it in the Approvals tab.');
    } catch (err) {
      alert(errorMessage(err, 'Failed to submit calendar event for approval'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label htmlFor="event-title" className={labelClass}>Title *</label>
        <input id="event-title" required maxLength={200} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} />
      </div>
      <div>
        <label htmlFor="event-description" className={labelClass}>Description <span className="text-muted-foreground">({form.description.length}/500)</span></label>
        <textarea id="event-description" rows={3} maxLength={500} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputClass} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="event-start" className={labelClass}>Start *</label>
          <input id="event-start" type="datetime-local" required value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} className={inputClass} />
        </div>
        <div>
          <label htmlFor="event-end" className={labelClass}>End *</label>
          <input id="event-end" type="datetime-local" required value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} className={inputClass} />
        </div>
      </div>
      {!event && (
        <div>
          <label htmlFor="event-recurrence" className={labelClass}>Repeats</label>
          <select id="event-recurrence" value={form.recurrence} onChange={(e) => setForm({ ...form, recurrence: e.target.value })} className={inputClass}>
            <option value="none">Does not repeat</option>
            <option value="daily">Daily (next 365 days)</option>
            <option value="weekly">Weekly (next 52 weeks)</option>
          </select>
        </div>
      )}
      <p className="text-xs text-muted-foreground">{event ? 'Edits' : 'New events'} go to Approvals before residents see them.</p>
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Submitting...' : event ? 'Save Changes' : 'Create Event'}</button>
        <button type="button" onClick={onCancel} className={btnSecondary}>Cancel</button>
      </div>
    </form>
  );
}

function CalendarTab({ condo, notify, onCountsChange }) {
  const api = useAdminApi();
  const [params, setParams] = useQueryParams();
  const [data, setData] = useState(null);

  const when = params.get('when') === 'past' ? 'past' : 'upcoming';
  const q = params.get('q') || '';
  const pageParam = Number(params.get('page')) || null;
  const selectedId = params.get('event');
  const creating = params.get('new') === '1';

  const load = useCallback(async () => {
    try {
      const response = await api.get(`/api/admin/condos/${condo._id}/calendar-events`, { when, q: q || undefined, page: pageParam || undefined, focus: pageParam ? undefined : selectedId || undefined });
      setData(response.data);
    } catch (err) {
      alert(errorMessage(err, 'Failed to fetch calendar events'));
    }
  }, [api, condo._id, when, q, pageParam, selectedId]);

  useEffect(() => { load(); }, [load]);

  const selected = data?.events.find((e) => String(e._id) === String(selectedId));

  const submitted = (message) => {
    notify(message);
    setParams({ new: null, event: null });
    onCountsChange();
  };

  const remove = async (event) => {
    let series = false;
    if (event.recurrenceGroupId) {
      const choice = window.prompt('This event is part of a recurring series.\nType "series" to delete the entire series, or "one" to delete only this occurrence.', 'one');
      if (choice === null) return;
      if (choice.trim().toLowerCase() === 'series') series = true;
      else if (choice.trim().toLowerCase() !== 'one') return;
    } else if (!window.confirm(`Delete event "${event.title}"?`)) {
      return;
    }
    try {
      await api.del(`/api/calendar/event/${event._id}${series ? '?series=true' : ''}`);
      notify(series ? 'Event series deleted.' : 'Event deleted.');
      setParams({ event: null });
      load();
    } catch (err) {
      alert(errorMessage(err, 'Failed to delete calendar event'));
    }
  };

  const columns = [
    {
      key: 'title',
      header: 'Title',
      className: 'font-medium text-foreground',
      render: (e) => <span className="flex items-center gap-2">{e.title}{e.recurrenceGroupId && <Badge>repeats</Badge>}</span>
    },
    { key: 'startTime', header: 'Starts', className: 'whitespace-nowrap', render: (e) => formatDateTime(e.startTime) },
    { key: 'endTime', header: 'Ends', hideBelow: 'md', className: 'whitespace-nowrap text-muted-foreground', render: (e) => formatDateTime(e.endTime) }
  ];

  const drawer = creating ? (
    <Drawer title="New event" onClose={() => setParams({ new: null })}>
      <EventForm condo={condo} onSaved={submitted} onCancel={() => setParams({ new: null })} />
    </Drawer>
  ) : selected ? (
    <Drawer title={selected.title} badges={selected.recurrenceGroupId && <Badge>repeats</Badge>} onClose={() => setParams({ event: null })}>
      <EventForm key={selected._id} condo={condo} event={selected} onSaved={submitted} onCancel={() => setParams({ event: null })} />
      <div className="mt-6 pt-4 border-t border-border">
        <button type="button" onClick={() => remove(selected)} className={btnDanger}>Delete event</button>
      </div>
    </Drawer>
  ) : null;

  const counts = data?.counts || {};

  return (
    <ListLayout drawer={drawer}>
      <Panel>
        <Toolbar>
          <SegmentedControl
            label="When"
            value={when}
            onChange={(value) => setParams({ when: value === 'upcoming' ? null : value, page: null })}
            options={[
              { value: 'upcoming', label: 'Upcoming', count: counts.upcoming },
              { value: 'past', label: 'Past', count: counts.past }
            ]}
          />
          <SearchInput value={q} onChange={(value) => setParams({ q: value, page: null }, { replace: true })} placeholder="Search events..." />
          <button type="button" onClick={() => setParams({ new: '1', event: null })} className={btnPrimary}>+ Add Event</button>
        </Toolbar>
        {!data ? <Spinner /> : (
          <>
            <DataTable
              label="Calendar events"
              columns={columns}
              rows={data.events}
              selectedId={selectedId}
              onRowClick={(e) => setParams({ event: e._id, new: null })}
              emptyMessage={q ? 'No events match your search.' : when === 'past' ? 'No past events.' : 'No upcoming events.'}
            />
            <Pagination page={data.page} pages={data.pages} total={data.total} limit={data.limit} onChange={(n) => setParams({ page: n === 1 ? null : n })} />
          </>
        )}
      </Panel>
    </ListLayout>
  );
}

export default CalendarTab;
